import UserAvatar from "@/components/shared/UserAvatar";

import { Checkbox } from "@primitives/checkbox";
import { useNewGroupChatState } from "@/zustand/NewGroupChatState";
import { prepareMemberForNewGroupChat } from "@/middleware/members";
import { useToast } from "@/hooks/useToast";
import { MAX_MEMBERS_IN_GROUP_CHAT_PER_PAYLOAD } from "@/config/constants";
import { useEffect, useRef } from "react";
import { CheckedState } from "@radix-ui/react-checkbox";
import { useTranslation } from "react-i18next";

type Props = {
  username: string;
  checked: boolean;
};

export default function SearchedMemberListItem({ username, checked }: Props) {
  const { t } = useTranslation();

  const controllerRef = useRef<AbortController | null>(null);
  const usernames = useNewGroupChatState((state) => state.usernames);
  const removeUsername = useNewGroupChatState((state) => state.removeUsername);
  const removeChatMember = useNewGroupChatState(
    (state) => state.removeChatMember,
  );

  const { toast } = useToast();

  const handleSelectMember = async (newCheckedVal: CheckedState) => {
    if (newCheckedVal) {
      if (usernames.length === MAX_MEMBERS_IN_GROUP_CHAT_PER_PAYLOAD - 1) {
        toast({
          duration: 1500,
          description: `You can only add up to ${MAX_MEMBERS_IN_GROUP_CHAT_PER_PAYLOAD} members once per group chat`,
          variant: "default",
        });
        return;
      }

      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;
      try {
        await prepareMemberForNewGroupChat(username, signal);
      } catch (error) {
        if (error instanceof Error && error.message === "canceled") {
          return;
        }

        toast({
          duration: 1000,
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    } else {
      controllerRef.current?.abort();
      removeUsername(username);
      removeChatMember(username);
    }
  };

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  return (
    <label
      className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle lg:cursor-pointer"
      htmlFor={username}
    >
      <UserAvatar username={username} className="size-8" />

      <h3 className="truncate">{username} </h3>
      <Checkbox
        className="ml-auto size-5 rounded-full"
        id={username}
        checked={checked}
        onCheckedChange={(value) => handleSelectMember(value)}
      />
    </label>
  );
}
